import { useState, useMemo } from "react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Pencil, Trash2, Activity, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp, ArrowUp, ArrowDown, Ruler } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PhysiotherapyEvolution {
  type: "physiotherapy_evolution";
  structure_type?: "physiotherapy_evolution" | "antropometry" | "sppb_tests" | "dynamometry_tests" | "goniometria";
  date: string;
  is_main_record: boolean;
  name?: string | null; // Nome do documento/plano (apenas para is_main_record)
  observations: string | null; // Coluna 2: Observações
  exercise: string | null; // Coluna 3: Exercício
  load: number | null; // Coluna 4: Carga
  load_unit?: string | null; // Unidade de medida da carga (Kg, cm, Un, m)
  reps: number | null; // Coluna 5: Repetições
  result: string | null; // Coluna 6: Resultado
  // Campos legados (manter para compatibilidade)
  sets?: number | null;
  pain_scale_eva?: number | null;
  metrics?: Array<{
    name: string;
    value: number;
    unit: string;
  }>;
  // Campos para antropometria
  measurement_type?: string; // Coluna 2: Dado
  value?: number; // Coluna 3: Medida
  unit?: string; // Coluna 4: Unidade
  // Campos para testes SPPB
  test_name?: string | null;
  time?: number | null;
  arms_used?: string | null;
  score?: number | null;
  balance_score?: number | null;
  gait_score?: number | null;
  chair_score?: number | null;
  total_score?: number | null;
  classification?: string | null;
  // Campos para testes de dinamometria
  dominant_hand?: string | null;
  right_value?: number | null;
  left_value?: number | null;
  observation?: string | null;
  asymmetry?: number | null;
  // Campos para goniometria
  movement_type?: string | null; // Tipo de movimento (ex: "Rotação", "Inclinação", "Flexão", "Extensão")
  body_part?: string | null; // Parte do corpo (ex: "cervical", "ombro", "joelho")
  side?: string | null; // Lado (ex: "direita", "esquerda")
  goniometry_value?: number | null; // Valor em graus
  goniometry_unit?: string | null; // Unidade (geralmente "graus" ou "°")
}

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  patient_id: string | null;
  status: string | null;
}

interface SmartfilePhysiotherapyEvolutionDetailsProps {
  session: SmartfileSession;
  onRefresh: () => void;
  onRecordsChange?: (updatedRecords: any[]) => void;
  expandAllByDefault?: boolean;
}

interface EditablePhysiotherapyEvolution extends PhysiotherapyEvolution {
  _index?: number;
}

export function SmartfilePhysiotherapyEvolutionDetails({ session, onRefresh, onRecordsChange, expandAllByDefault = false }: SmartfilePhysiotherapyEvolutionDetailsProps) {
  const isMobile = useIsMobile();

  // Buscar o registro principal de evolução fisioterapêutica (is_main_record: true, structure_type: "physiotherapy_evolution") - memoizado
  const mainRecord = useMemo(() => {
    return (session.structured_records || []).find((record: any) =>
      typeof record === 'object' &&
      record !== null &&
      'type' in record &&
      record.type === 'physiotherapy_evolution' &&
      ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
      (!(record as any).structure_type || (record as any).structure_type === 'physiotherapy_evolution')
    ) as PhysiotherapyEvolution | undefined;
  }, [session.structured_records]);

  // Buscar o registro principal de antropometria (is_main_record: true, structure_type: "antropometry") - memoizado
  const mainAnthropometryRecord = useMemo(() => {
    return (session.structured_records || []).find((record: any) =>
      typeof record === 'object' &&
      record !== null &&
      'type' in record &&
      record.type === 'physiotherapy_evolution' &&
      ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
      (record as any).structure_type === 'antropometry'
    ) as PhysiotherapyEvolution | undefined;
  }, [session.structured_records]);

  // Separar registros por structure_type (excluir registros principais) - memoizado
  const allRecords = useMemo(() => {
    return (session.structured_records || [])
      .filter((record: any): record is PhysiotherapyEvolution =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'physiotherapy_evolution' &&
        !(record as any).is_main_record // Excluir registros principais
      );
  }, [session.structured_records]);

  // Filtrar evolução fisioterapêutica (structure_type: "physiotherapy_evolution" ou sem structure_type para compatibilidade) - memoizado
  const physioRecords: EditablePhysiotherapyEvolution[] = useMemo(() => {
    return allRecords
      .filter((record: PhysiotherapyEvolution) =>
        !record.structure_type || record.structure_type === 'physiotherapy_evolution'
      )
      .map((record: PhysiotherapyEvolution, index: number) => ({
        ...record,
        _index: index
      }));
  }, [allRecords]);

  // Filtrar antropometria (structure_type: "antropometry") - memoizado
  // Excluir o registro principal (is_main_record) para evitar duplicação
  const anthropometryRecords: EditablePhysiotherapyEvolution[] = useMemo(() => {
    return allRecords
      .filter((record: PhysiotherapyEvolution) => {
        const isMainRecord = record.is_main_record === true || (record as any).is_main_record === 'true';
        return record.structure_type === 'antropometry' && !isMainRecord;
      })
      .map((record: PhysiotherapyEvolution, index: number) => ({
        ...record,
        _index: index
      }));
  }, [allRecords]);

  // Buscar o registro principal de testes SPPB (is_main_record: true, structure_type: "sppb_tests") - memoizado
  const mainSppbRecord = useMemo(() => {
    return (session.structured_records || []).find((record: any) =>
      typeof record === 'object' &&
      record !== null &&
      'type' in record &&
      record.type === 'physiotherapy_evolution' &&
      ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
      (record as any).structure_type === 'sppb_tests'
    ) as PhysiotherapyEvolution | undefined;
  }, [session.structured_records]);

  // Filtrar testes SPPB (structure_type: "sppb_tests") - memoizado
  // Excluir o registro principal (is_main_record) para evitar duplicação
  const sppbRecords: EditablePhysiotherapyEvolution[] = useMemo(() => {
    return allRecords
      .filter((record: PhysiotherapyEvolution) => {
        const isMainRecord = record.is_main_record === true || (record as any).is_main_record === 'true';
        return record.structure_type === 'sppb_tests' && !isMainRecord;
      })
      .map((record: PhysiotherapyEvolution, index: number) => ({
        ...record,
        _index: index
      }));
  }, [allRecords]);

  // Buscar o registro principal de testes de dinamometria (is_main_record: true, structure_type: "dynamometry_tests") - memoizado
  const mainDynamometryRecord = useMemo(() => {
    return (session.structured_records || []).find((record: any) =>
      typeof record === 'object' &&
      record !== null &&
      'type' in record &&
      record.type === 'physiotherapy_evolution' &&
      ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
      (record as any).structure_type === 'dynamometry_tests'
    ) as PhysiotherapyEvolution | undefined;
  }, [session.structured_records]);

  // Filtrar testes de dinamometria (structure_type: "dynamometry_tests") - memoizado
  // Excluir o registro principal (is_main_record) para evitar duplicação
  const dynamometryRecords: EditablePhysiotherapyEvolution[] = useMemo(() => {
    return allRecords
      .filter((record: PhysiotherapyEvolution) => {
        const isMainRecord = record.is_main_record === true || (record as any).is_main_record === 'true';
        return record.structure_type === 'dynamometry_tests' && !isMainRecord;
      })
      .map((record: PhysiotherapyEvolution, index: number) => ({
        ...record,
        _index: index
      }));
  }, [allRecords]);

  // Buscar o registro principal de goniometria (is_main_record: true, structure_type: "goniometria") - memoizado
  const mainGoniometryRecord = useMemo(() => {
    return (session.structured_records || []).find((record: any) =>
      typeof record === 'object' &&
      record !== null &&
      'type' in record &&
      record.type === 'physiotherapy_evolution' &&
      ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
      (record as any).structure_type === 'goniometria'
    ) as PhysiotherapyEvolution | undefined;
  }, [session.structured_records]);

  // Filtrar goniometria (structure_type: "goniometria") - memoizado
  // Excluir o registro principal (is_main_record) para evitar duplicação
  const goniometryRecords: EditablePhysiotherapyEvolution[] = useMemo(() => {
    return allRecords
      .filter((record: PhysiotherapyEvolution) => {
        const isMainRecord = record.is_main_record === true || (record as any).is_main_record === 'true';
        return record.structure_type === 'goniometria' && !isMainRecord;
      })
      .map((record: PhysiotherapyEvolution, index: number) => ({
        ...record,
        _index: index
      }));
  }, [allRecords]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingMainRecord, setEditingMainRecord] = useState(false);
  const [editedRecords, setEditedRecords] = useState<EditablePhysiotherapyEvolution[]>(physioRecords);
  const [editedMainRecord, setEditedMainRecord] = useState<PhysiotherapyEvolution | undefined>(mainRecord);
  const [isSaving, setIsSaving] = useState(false);
  const [dateSortOrder, setDateSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedDateGroups, setExpandedDateGroups] = useState<Map<string, boolean>>(new Map());

  // Atualizar editedRecords quando physioRecords mudar
  React.useEffect(() => {
    setEditedRecords(physioRecords);
    setEditedMainRecord(mainRecord);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.structured_records]);

  const handleSave = async (index: number) => {
    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice real no array completo
      let physioCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'physiotherapy_evolution' &&
          !(record as any).is_main_record &&
          (!(record as any).structure_type || (record as any).structure_type === 'physiotherapy_evolution')) {
          if (physioCount === index) {
            realIndex = i;
            break;
          }
          physioCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = editedRecords[index];

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        // Chamar onRecordsChange se fornecido (para Step2Analyze)
        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro atualizado com sucesso!");
        setEditingIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice real no array completo
      let physioCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'physiotherapy_evolution' &&
          !(record as any).is_main_record &&
          (!(record as any).structure_type || (record as any).structure_type === 'physiotherapy_evolution')) {
          if (physioCount === index) {
            realIndex = i;
            break;
          }
          physioCount++;
        }
      }

      if (realIndex !== -1) {
        // Remover o registro do array
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        // Chamar onRecordsChange se fornecido (para Step2Analyze)
        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro excluído com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir registro");
    }
  };

  const handleCancel = () => {
    setEditedRecords(physioRecords);
    setEditingIndex(null);
  };

  const handleSaveMainRecord = async () => {
    setIsSaving(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice do registro principal
      const mainRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'physiotherapy_evolution' &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true')
      );

      if (mainRecordIndex !== -1 && editedMainRecord) {
        updatedRecords[mainRecordIndex] = editedMainRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Nome do documento atualizado com sucesso!");
        setEditingMainRecord(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelMainRecord = () => {
    setEditedMainRecord(mainRecord);
    setEditingMainRecord(false);
  };

  // Estados para antropometria
  const [editingAnthropometryIndex, setEditingAnthropometryIndex] = useState<number | null>(null);
  const [editingMainAnthropometryRecord, setEditingMainAnthropometryRecord] = useState(false);
  const [editedAnthropometryRecords, setEditedAnthropometryRecords] = useState<EditablePhysiotherapyEvolution[]>(anthropometryRecords);
  const [editedMainAnthropometryRecord, setEditedMainAnthropometryRecord] = useState<PhysiotherapyEvolution | undefined>(mainAnthropometryRecord);
  const [isSavingAnthropometry, setIsSavingAnthropometry] = useState(false);
  const [dateSortOrderAnthropometry, setDateSortOrderAnthropometry] = useState<'asc' | 'desc'>('desc');
  const [expandedDateGroupsAnthropometry, setExpandedDateGroupsAnthropometry] = useState<Map<string, boolean>>(new Map());

  // Atualizar editedAnthropometryRecords quando anthropometryRecords mudar
  React.useEffect(() => {
    setEditedAnthropometryRecords(anthropometryRecords);
    setEditedMainAnthropometryRecord(mainAnthropometryRecord);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.structured_records]);

  const handleSaveAnthropometry = async (index: number) => {
    setIsSavingAnthropometry(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice real no array completo
      let anthropometryCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'physiotherapy_evolution' &&
          !(record as any).is_main_record &&
          (record as any).structure_type === 'antropometry') {
          if (anthropometryCount === index) {
            realIndex = i;
            break;
          }
          anthropometryCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = editedAnthropometryRecords[index];

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro de antropometria atualizado com sucesso!");
        setEditingAnthropometryIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSavingAnthropometry(false);
    }
  };

  const handleDeleteAnthropometry = async (index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let anthropometryCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'physiotherapy_evolution' &&
          !(record as any).is_main_record &&
          (record as any).structure_type === 'antropometry') {
          if (anthropometryCount === index) {
            realIndex = i;
            break;
          }
          anthropometryCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro de antropometria excluído com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir registro");
    }
  };

  const handleCancelAnthropometry = () => {
    setEditedAnthropometryRecords(anthropometryRecords);
    setEditingAnthropometryIndex(null);
  };

  const handleSaveMainAnthropometryRecord = async () => {
    setIsSavingAnthropometry(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      // Encontrar o índice do registro principal de antropometria
      const mainAnthropometryRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'physiotherapy_evolution' &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
        (record as any).structure_type === 'antropometry'
      );

      if (mainAnthropometryRecordIndex !== -1 && editedMainAnthropometryRecord) {
        updatedRecords[mainAnthropometryRecordIndex] = editedMainAnthropometryRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Nome do documento de antropometria atualizado com sucesso!");
        setEditingMainAnthropometryRecord(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSavingAnthropometry(false);
    }
  };

  const handleCancelMainAnthropometryRecord = () => {
    setEditedMainAnthropometryRecord(mainAnthropometryRecord);
    setEditingMainAnthropometryRecord(false);
  };

  // Estados para testes SPPB
  const [editingSppbIndex, setEditingSppbIndex] = useState<number | null>(null);
  const [editingMainSppbRecord, setEditingMainSppbRecord] = useState(false);
  const [editedSppbRecords, setEditedSppbRecords] = useState<EditablePhysiotherapyEvolution[]>(sppbRecords);
  const [editedMainSppbRecord, setEditedMainSppbRecord] = useState<PhysiotherapyEvolution | undefined>(mainSppbRecord);
  const [isSavingSppb, setIsSavingSppb] = useState(false);
  const [dateSortOrderSppb, setDateSortOrderSppb] = useState<'asc' | 'desc'>('desc');
  const [expandedDateGroupsSppb, setExpandedDateGroupsSppb] = useState<Map<string, boolean>>(new Map());

  // Atualizar editedSppbRecords quando sppbRecords mudar
  React.useEffect(() => {
    setEditedSppbRecords(sppbRecords);
    setEditedMainSppbRecord(mainSppbRecord);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.structured_records]);

  const handleSaveSppb = async (index: number) => {
    setIsSavingSppb(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let sppbCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'physiotherapy_evolution' &&
          !(record as any).is_main_record &&
          (record as any).structure_type === 'sppb_tests') {
          if (sppbCount === index) {
            realIndex = i;
            break;
          }
          sppbCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = editedSppbRecords[index];

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro de teste SPPB atualizado com sucesso!");
        setEditingSppbIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSavingSppb(false);
    }
  };

  const handleDeleteSppb = async (index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let sppbCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'physiotherapy_evolution' &&
          !(record as any).is_main_record &&
          (record as any).structure_type === 'sppb_tests') {
          if (sppbCount === index) {
            realIndex = i;
            break;
          }
          sppbCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro de teste SPPB excluído com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir registro");
    }
  };

  const handleCancelSppb = () => {
    setEditedSppbRecords(sppbRecords);
    setEditingSppbIndex(null);
  };

  const handleSaveMainSppbRecord = async () => {
    setIsSavingSppb(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      const mainSppbRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'physiotherapy_evolution' &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
        (record as any).structure_type === 'sppb_tests'
      );

      if (mainSppbRecordIndex !== -1 && editedMainSppbRecord) {
        updatedRecords[mainSppbRecordIndex] = editedMainSppbRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Nome do documento de testes SPPB atualizado com sucesso!");
        setEditingMainSppbRecord(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSavingSppb(false);
    }
  };

  const handleCancelMainSppbRecord = () => {
    setEditedMainSppbRecord(mainSppbRecord);
    setEditingMainSppbRecord(false);
  };

  // Estados para testes de dinamometria
  const [editingDynamometryIndex, setEditingDynamometryIndex] = useState<number | null>(null);
  const [editingMainDynamometryRecord, setEditingMainDynamometryRecord] = useState(false);
  const [editedDynamometryRecords, setEditedDynamometryRecords] = useState<EditablePhysiotherapyEvolution[]>(dynamometryRecords);
  const [editedMainDynamometryRecord, setEditedMainDynamometryRecord] = useState<PhysiotherapyEvolution | undefined>(mainDynamometryRecord);
  const [isSavingDynamometry, setIsSavingDynamometry] = useState(false);
  const [dateSortOrderDynamometry, setDateSortOrderDynamometry] = useState<'asc' | 'desc'>('desc');
  const [expandedDateGroupsDynamometry, setExpandedDateGroupsDynamometry] = useState<Map<string, boolean>>(new Map());

  // Atualizar editedDynamometryRecords quando dynamometryRecords mudar
  React.useEffect(() => {
    setEditedDynamometryRecords(dynamometryRecords);
    setEditedMainDynamometryRecord(mainDynamometryRecord);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.structured_records]);

  const handleSaveDynamometry = async (index: number) => {
    setIsSavingDynamometry(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let dynamometryCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'physiotherapy_evolution' &&
          !(record as any).is_main_record &&
          (record as any).structure_type === 'dynamometry_tests') {
          if (dynamometryCount === index) {
            realIndex = i;
            break;
          }
          dynamometryCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = editedDynamometryRecords[index];

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro de teste de dinamometria atualizado com sucesso!");
        setEditingDynamometryIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSavingDynamometry(false);
    }
  };

  const handleDeleteDynamometry = async (index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let dynamometryCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'physiotherapy_evolution' &&
          !(record as any).is_main_record &&
          (record as any).structure_type === 'dynamometry_tests') {
          if (dynamometryCount === index) {
            realIndex = i;
            break;
          }
          dynamometryCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro de teste de dinamometria excluído com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir registro");
    }
  };

  const handleCancelDynamometry = () => {
    setEditedDynamometryRecords(dynamometryRecords);
    setEditingDynamometryIndex(null);
  };

  const handleSaveMainDynamometryRecord = async () => {
    setIsSavingDynamometry(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      const mainDynamometryRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'physiotherapy_evolution' &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
        (record as any).structure_type === 'dynamometry_tests'
      );

      if (mainDynamometryRecordIndex !== -1 && editedMainDynamometryRecord) {
        updatedRecords[mainDynamometryRecordIndex] = editedMainDynamometryRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Nome do documento de testes de dinamometria atualizado com sucesso!");
        setEditingMainDynamometryRecord(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSavingDynamometry(false);
    }
  };

  const handleCancelMainDynamometryRecord = () => {
    setEditedMainDynamometryRecord(mainDynamometryRecord);
    setEditingMainDynamometryRecord(false);
  };

  // Estados para goniometria
  const [editingGoniometryIndex, setEditingGoniometryIndex] = useState<number | null>(null);
  const [editingMainGoniometryRecord, setEditingMainGoniometryRecord] = useState(false);
  const [editedGoniometryRecords, setEditedGoniometryRecords] = useState<EditablePhysiotherapyEvolution[]>(goniometryRecords);
  const [editedMainGoniometryRecord, setEditedMainGoniometryRecord] = useState<PhysiotherapyEvolution | undefined>(mainGoniometryRecord);
  const [isSavingGoniometry, setIsSavingGoniometry] = useState(false);
  const [dateSortOrderGoniometry, setDateSortOrderGoniometry] = useState<'asc' | 'desc'>('desc');
  const [expandedDateGroupsGoniometry, setExpandedDateGroupsGoniometry] = useState<Map<string, boolean>>(new Map());

  // Atualizar editedGoniometryRecords quando goniometryRecords mudar
  React.useEffect(() => {
    setEditedGoniometryRecords(goniometryRecords);
    setEditedMainGoniometryRecord(mainGoniometryRecord);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.structured_records]);

  const handleSaveGoniometry = async (index: number) => {
    setIsSavingGoniometry(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let goniometryCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'physiotherapy_evolution' &&
          !(record as any).is_main_record &&
          (record as any).structure_type === 'goniometria') {
          if (goniometryCount === index) {
            realIndex = i;
            break;
          }
          goniometryCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords[realIndex] = editedGoniometryRecords[index];

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro de goniometria atualizado com sucesso!");
        setEditingGoniometryIndex(null);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSavingGoniometry(false);
    }
  };

  const handleDeleteGoniometry = async (index: number) => {
    try {
      const updatedRecords = [...(session.structured_records || [])];

      let goniometryCount = 0;
      let realIndex = -1;

      for (let i = 0; i < updatedRecords.length; i++) {
        const record = updatedRecords[i];
        if (typeof record === 'object' && record !== null && 'type' in record &&
          record.type === 'physiotherapy_evolution' &&
          !(record as any).is_main_record &&
          (record as any).structure_type === 'goniometria') {
          if (goniometryCount === index) {
            realIndex = i;
            break;
          }
          goniometryCount++;
        }
      }

      if (realIndex !== -1) {
        updatedRecords.splice(realIndex, 1);

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Registro de goniometria excluído com sucesso!");
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao excluir:', error);
      toast.error("❌ Erro ao excluir registro");
    }
  };

  const handleCancelGoniometry = () => {
    setEditedGoniometryRecords(goniometryRecords);
    setEditingGoniometryIndex(null);
  };

  const handleSaveMainGoniometryRecord = async () => {
    setIsSavingGoniometry(true);
    try {
      const updatedRecords = [...(session.structured_records || [])];

      const mainGoniometryRecordIndex = updatedRecords.findIndex((record: any) =>
        typeof record === 'object' &&
        record !== null &&
        'type' in record &&
        record.type === 'physiotherapy_evolution' &&
        ((record as any).is_main_record === true || (record as any).is_main_record === 'true') &&
        (record as any).structure_type === 'goniometria'
      );

      if (mainGoniometryRecordIndex !== -1 && editedMainGoniometryRecord) {
        updatedRecords[mainGoniometryRecordIndex] = editedMainGoniometryRecord;

        const { error } = await supabase
          .from('smartfile_sessions')
          .update({ structured_records: updatedRecords as any })
          .eq('id', session.id);

        if (error) throw error;

        if (onRecordsChange) {
          onRecordsChange(updatedRecords);
        }

        toast.success("✅ Nome do documento de goniometria atualizado com sucesso!");
        setEditingMainGoniometryRecord(false);
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast.error("❌ Erro ao salvar alterações");
    } finally {
      setIsSavingGoniometry(false);
    }
  };

  const handleCancelMainGoniometryRecord = () => {
    setEditedMainGoniometryRecord(mainGoniometryRecord);
    setEditingMainGoniometryRecord(false);
  };

  // Função auxiliar para formatar datas de forma segura
  const formatDateSafe = (dateString: string | null | undefined, fallback: string = 'Data inválida'): string => {
    if (!dateString) return fallback;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return fallback;
      return format(date, "dd/MM/yyyy");
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return fallback;
    }
  };

  // Verificar se há algo para exibir (registro principal ou sub-registros)
  const hasContentToShow = mainRecord || mainAnthropometryRecord || mainSppbRecord || mainDynamometryRecord || mainGoniometryRecord || 
    physioRecords.length > 0 || anthropometryRecords.length > 0 || sppbRecords.length > 0 || dynamometryRecords.length > 0 || goniometryRecords.length > 0;

  if (!hasContentToShow) {
    return (
      <div className="text-center py-8 text-muted-foreground bg-background border-t">
        <p className="text-sm">Nenhuma evolução fisioterapêutica, antropometria, testes SPPB, testes de dinamometria ou goniometria encontrada nesta sessão</p>
      </div>
    );
  }

  // Agrupar evolução fisioterapêutica por mês/ano
  const groupedByDate = physioRecords.reduce((acc, record, index) => {
    try {
      const date = record.date || session.created_at;
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        console.warn('Data inválida encontrada:', date);
        return acc;
      }
      const monthYearKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
      const displayKey = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

      if (!acc[monthYearKey]) {
        acc[monthYearKey] = {
          displayKey,
          records: []
        };
      }
      acc[monthYearKey].records.push({ ...record, _index: index });
    } catch (error) {
      console.error('Erro ao processar registro de fisioterapia:', error, record);
    }
    return acc;
  }, {} as Record<string, { displayKey: string; records: EditablePhysiotherapyEvolution[] }>);

  // Agrupar antropometria por mês/ano
  const groupedByDateAnthropometry = anthropometryRecords.reduce((acc, record, index) => {
    try {
      const date = record.date || session.created_at;
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        console.warn('Data inválida encontrada:', date);
        return acc;
      }
      const monthYearKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
      const displayKey = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

      if (!acc[monthYearKey]) {
        acc[monthYearKey] = {
          displayKey,
          records: []
        };
      }
      acc[monthYearKey].records.push({ ...record, _index: index });
    } catch (error) {
      console.error('Erro ao processar registro de antropometria:', error, record);
    }
    return acc;
  }, {} as Record<string, { displayKey: string; records: EditablePhysiotherapyEvolution[] }>);

  // Ordenar por mês/ano conforme o estado de ordenação
  const sortedDates = Object.keys(groupedByDate).sort((a, b) => {
    const dateA = new Date(a + '-01').getTime();
    const dateB = new Date(b + '-01').getTime();
    return dateSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const sortedDatesAnthropometry = Object.keys(groupedByDateAnthropometry).sort((a, b) => {
    const dateA = new Date(a + '-01').getTime();
    const dateB = new Date(b + '-01').getTime();
    return dateSortOrderAnthropometry === 'asc' ? dateA - dateB : dateB - dateA;
  });

  // Agrupar testes SPPB por mês/ano
  const groupedByDateSppb = sppbRecords.reduce((acc, record, index) => {
    try {
      const date = record.date || session.created_at;
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        console.warn('Data inválida encontrada:', date);
        return acc;
      }
      const monthYearKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
      const displayKey = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

      if (!acc[monthYearKey]) {
        acc[monthYearKey] = {
          displayKey,
          records: []
        };
      }
      acc[monthYearKey].records.push({ ...record, _index: index });
    } catch (error) {
      console.error('Erro ao processar registro de teste SPPB:', error, record);
    }
    return acc;
  }, {} as Record<string, { displayKey: string; records: EditablePhysiotherapyEvolution[] }>);

  // Agrupar testes de dinamometria por mês/ano
  const groupedByDateDynamometry = dynamometryRecords.reduce((acc, record, index) => {
    try {
      const date = record.date || session.created_at;
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        console.warn('Data inválida encontrada:', date);
        return acc;
      }
      const monthYearKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
      const displayKey = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

      if (!acc[monthYearKey]) {
        acc[monthYearKey] = {
          displayKey,
          records: []
        };
      }
      acc[monthYearKey].records.push({ ...record, _index: index });
    } catch (error) {
      console.error('Erro ao processar registro de teste de dinamometria:', error, record);
    }
    return acc;
  }, {} as Record<string, { displayKey: string; records: EditablePhysiotherapyEvolution[] }>);

  const sortedDatesSppb = Object.keys(groupedByDateSppb).sort((a, b) => {
    const dateA = new Date(a + '-01').getTime();
    const dateB = new Date(b + '-01').getTime();
    return dateSortOrderSppb === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const sortedDatesDynamometry = Object.keys(groupedByDateDynamometry).sort((a, b) => {
    const dateA = new Date(a + '-01').getTime();
    const dateB = new Date(b + '-01').getTime();
    return dateSortOrderDynamometry === 'asc' ? dateA - dateB : dateB - dateA;
  });

  // Agrupar goniometria por mês/ano
  const groupedByDateGoniometry = goniometryRecords.reduce((acc, record, index) => {
    try {
      const date = record.date || session.created_at;
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        console.warn('Data inválida encontrada:', date);
        return acc;
      }
      const monthYearKey = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}`;
      const displayKey = `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${dateObj.getFullYear()}`;

      if (!acc[monthYearKey]) {
        acc[monthYearKey] = {
          displayKey,
          records: []
        };
      }
      acc[monthYearKey].records.push({ ...record, _index: index });
    } catch (error) {
      console.error('Erro ao processar registro de goniometria:', error, record);
    }
    return acc;
  }, {} as Record<string, { displayKey: string; records: EditablePhysiotherapyEvolution[] }>);

  const sortedDatesGoniometry = Object.keys(groupedByDateGoniometry).sort((a, b) => {
    const dateA = new Date(a + '-01').getTime();
    const dateB = new Date(b + '-01').getTime();
    return dateSortOrderGoniometry === 'asc' ? dateA - dateB : dateB - dateA;
  });

  // Toggle de expansão de grupos de data
  const toggleDateGroup = (dateKey: string, isOpen: boolean) => {
    setExpandedDateGroups((prev) => {
      const next = new Map(prev);
      next.set(dateKey, isOpen);
      return next;
    });
  };

  // Toggle de ordenação de data
  const toggleDateSort = () => {
    setDateSortOrder((prev) => prev === 'asc' ? 'desc' : 'asc');
  };

  const toggleDateSortAnthropometry = () => {
    setDateSortOrderAnthropometry((prev) => prev === 'asc' ? 'desc' : 'asc');
  };

  const toggleDateGroupAnthropometry = (dateKey: string, isOpen: boolean) => {
    setExpandedDateGroupsAnthropometry((prev) => {
      const next = new Map(prev);
      next.set(dateKey, isOpen);
      return next;
    });
  };

  const toggleDateGroupSppb = (dateKey: string, isOpen: boolean) => {
    setExpandedDateGroupsSppb((prev) => {
      const next = new Map(prev);
      next.set(dateKey, isOpen);
      return next;
    });
  };

  const toggleDateGroupDynamometry = (dateKey: string, isOpen: boolean) => {
    setExpandedDateGroupsDynamometry((prev) => {
      const next = new Map(prev);
      next.set(dateKey, isOpen);
      return next;
    });
  };

  const toggleDateGroupGoniometry = (dateKey: string, isOpen: boolean) => {
    setExpandedDateGroupsGoniometry((prev) => {
      const next = new Map(prev);
      next.set(dateKey, isOpen);
      return next;
    });
  };

  const toggleDateSortGoniometry = () => {
    setDateSortOrderGoniometry((prev) => prev === 'asc' ? 'desc' : 'asc');
  };

  // Função para renderizar uma linha de registro
  const renderRecordRow = (record: EditablePhysiotherapyEvolution, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingIndex === originalIndex;

    return (
      <React.Fragment key={`${dateKey}-${originalIndex}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={8} className="p-3 bg-muted/30">
              <div className="space-y-3">
                {/* Ordem: Data, Observações, Exercício, Carga, Repetições, Resultado */}
                <div>
                  <Label className="text-xs">Data</Label>
                  <Input
                    type="date"
                    value={editedRecords[originalIndex]?.date || ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = {
                        ...updated[originalIndex],
                        date: e.target.value
                      };
                      setEditedRecords(updated);
                    }}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Observações</Label>
                  <Textarea
                    value={editedRecords[originalIndex]?.observations || ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = {
                        ...updated[originalIndex],
                        observations: e.target.value || null
                      };
                      setEditedRecords(updated);
                    }}
                    className="min-h-[60px]"
                    placeholder="Observações sobre a evolução..."
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs">Exercício</Label>
                    <Input
                      value={editedRecords[originalIndex]?.exercise || ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          exercise: e.target.value || null
                        };
                        setEditedRecords(updated);
                      }}
                      placeholder="Ex: T Y W"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Carga</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedRecords[originalIndex]?.load ?? ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          load: e.target.value ? parseFloat(e.target.value) : null
                        };
                        setEditedRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Unidade de Medida</Label>
                    <Select
                      value={editedRecords[originalIndex]?.load_unit || 'Kg'}
                      onValueChange={(value) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          load_unit: value || null
                        };
                        setEditedRecords(updated);
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Selecione a unidade" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Kg">Kg</SelectItem>
                        <SelectItem value="cm">cm</SelectItem>
                        <SelectItem value="Un">Un</SelectItem>
                        <SelectItem value="m">m</SelectItem>
                        <SelectItem value="s">s</SelectItem>
                        <SelectItem value="°">°</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Repetições</Label>
                    <Input
                      type="number"
                      value={editedRecords[originalIndex]?.reps ?? ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          reps: e.target.value ? parseInt(e.target.value) : null
                        };
                        setEditedRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Resultado</Label>
                    <Input
                      value={editedRecords[originalIndex]?.result || ''}
                      onChange={(e) => {
                        const updated = [...editedRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          result: e.target.value || null
                        };
                        setEditedRecords(updated);
                      }}
                      placeholder="Resultado do exercício"
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSave(originalIndex)} disabled={isSaving}>
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            {/* Ordem: Data, Observações, Exercício, Carga, Repetições, Resultado */}
            <td className="p-3 text-sm text-muted-foreground w-[120px]">
              <div className="truncate">{formatDateSafe(record.date, '–')}</div>
            </td>
            <td className="p-3 w-[200px]">
              <div className="text-sm text-muted-foreground truncate" title={record.observations || ''}>
                {record.observations || '–'}
              </div>
            </td>
            <td className="p-3 w-[150px]">
              <div className="text-sm font-medium truncate" title={record.exercise || '–'}>
                {record.exercise || '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.load !== null && record.load !== undefined ? record.load : '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.load !== null && record.load !== undefined
                  ? (record.load_unit || 'Kg')
                  : '–'}
              </div>
            </td>
            <td className="p-3 w-[120px]">
              <div className="text-sm truncate">
                {record.reps !== null ? record.reps : '–'}
              </div>
            </td>
            <td className="p-3 w-[150px]">
              <div className="text-sm truncate" title={record.result || ''}>
                {record.result || '–'}
              </div>
            </td>
            <td className="p-3 text-right w-[120px]">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingIndex(originalIndex)}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir esta evolução?
                        <br />
                        <span className="font-medium mt-2 block">
                          {formatDateSafe(record.date, 'Data inválida')} - {record.exercise || 'Sem exercício'}
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(originalIndex)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar linha de antropometria
  const renderAnthropometryRow = (record: EditablePhysiotherapyEvolution, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingAnthropometryIndex === originalIndex;

    const measurementTypeLabels: Record<string, string> = {
      peso: 'Peso',
      altura: 'Altura',
      circunferencia_cintura: 'Circunferência da Cintura',
      circunferencia_quadril: 'Circunferência do Quadril',
      imc: 'IMC',
      gordura_corporal: 'Gordura Corporal'
    };

    return (
      <React.Fragment key={`${dateKey}-anthro-${originalIndex}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={5} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Data</Label>
                    <Input
                      type="date"
                      value={editedAnthropometryRecords[originalIndex]?.date || ''}
                      onChange={(e) => {
                        const updated = [...editedAnthropometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          date: e.target.value
                        };
                        setEditedAnthropometryRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  {/* Ordem: Data, Dado, Medida, Unidade */}
                  <div>
                    <Label className="text-xs">Dado</Label>
                    <Input
                      value={editedAnthropometryRecords[originalIndex]?.measurement_type || ''}
                      onChange={(e) => {
                        const updated = [...editedAnthropometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          measurement_type: e.target.value || undefined
                        };
                        setEditedAnthropometryRecords(updated);
                      }}
                      placeholder="Ex: Peso, Altura"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Medida</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedAnthropometryRecords[originalIndex]?.value ?? ''}
                      onChange={(e) => {
                        const updated = [...editedAnthropometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          value: e.target.value ? parseFloat(e.target.value) : undefined
                        };
                        setEditedAnthropometryRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Unidade</Label>
                    <Input
                      value={editedAnthropometryRecords[originalIndex]?.unit || ''}
                      onChange={(e) => {
                        const updated = [...editedAnthropometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          unit: e.target.value || undefined
                        };
                        setEditedAnthropometryRecords(updated);
                      }}
                      placeholder="kg, cm, m, %"
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSaveAnthropometry(originalIndex)} disabled={isSavingAnthropometry}>
                    {isSavingAnthropometry ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelAnthropometry} disabled={isSavingAnthropometry}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground w-[120px]">
              <div className="truncate">{formatDateSafe(record.date, '–')}</div>
            </td>
            <td className="p-3 w-[200px]">
              <div className="text-sm font-medium truncate">
                {measurementTypeLabels[record.measurement_type || ''] || record.measurement_type || '–'}
              </div>
            </td>
            <td className="p-3 w-[120px]">
              <div className="text-sm truncate">
                {record.value !== undefined && record.value !== null ? record.value : '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.unit || '–'}
              </div>
            </td>
            <td className="p-3 text-right w-[120px]">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingAnthropometryIndex(originalIndex)}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir esta medida antropométrica?
                        <br />
                        <span className="font-medium mt-2 block">
                          {formatDateSafe(record.date, 'Data inválida')} - {measurementTypeLabels[record.measurement_type || ''] || record.measurement_type || 'Medida'}
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDeleteAnthropometry(originalIndex)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar card mobile de evolução fisioterapêutica
  const renderMobilePhysioCard = (record: EditablePhysiotherapyEvolution, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingIndex === originalIndex;

    return (
      <Card key={`${dateKey}-${originalIndex}-mobile`} className="mb-3">
        <CardContent className="p-4">
          {isEditing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={editedRecords[originalIndex]?.date || ''}
                  onChange={(e) => {
                    const updated = [...editedRecords];
                    updated[originalIndex] = { ...updated[originalIndex], date: e.target.value };
                    setEditedRecords(updated);
                  }}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Observações</Label>
                <Textarea
                  value={editedRecords[originalIndex]?.observations || ''}
                  onChange={(e) => {
                    const updated = [...editedRecords];
                    updated[originalIndex] = { ...updated[originalIndex], observations: e.target.value || null };
                    setEditedRecords(updated);
                  }}
                  className="min-h-[60px]"
                  placeholder="Observações sobre a evolução..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Exercício</Label>
                  <Input
                    value={editedRecords[originalIndex]?.exercise || ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = { ...updated[originalIndex], exercise: e.target.value || null };
                      setEditedRecords(updated);
                    }}
                    placeholder="Ex: T Y W"
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Carga</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editedRecords[originalIndex]?.load ?? ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = { ...updated[originalIndex], load: e.target.value ? parseFloat(e.target.value) : null };
                      setEditedRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Unidade</Label>
                  <Select
                    value={editedRecords[originalIndex]?.load_unit || 'Kg'}
                    onValueChange={(value) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = { ...updated[originalIndex], load_unit: value || null };
                      setEditedRecords(updated);
                    }}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Unidade" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Kg">Kg</SelectItem>
                      <SelectItem value="cm">cm</SelectItem>
                      <SelectItem value="Un">Un</SelectItem>
                      <SelectItem value="m">m</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Repetições</Label>
                  <Input
                    type="number"
                    value={editedRecords[originalIndex]?.reps ?? ''}
                    onChange={(e) => {
                      const updated = [...editedRecords];
                      updated[originalIndex] = { ...updated[originalIndex], reps: e.target.value ? parseInt(e.target.value) : null };
                      setEditedRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Resultado</Label>
                <Input
                  value={editedRecords[originalIndex]?.result || ''}
                  onChange={(e) => {
                    const updated = [...editedRecords];
                    updated[originalIndex] = { ...updated[originalIndex], result: e.target.value || null };
                    setEditedRecords(updated);
                  }}
                  placeholder="Resultado do exercício"
                  className="h-9"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => handleSave(originalIndex)} disabled={isSaving} className="flex-1">
                  {isSaving ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancel} disabled={isSaving} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="h-4 w-4 text-muted-foreground" />
                    <h4 className="font-semibold text-sm leading-tight">{record.exercise || 'Exercício não especificado'}</h4>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Calendar className="h-3 w-3" />
                    {formatDateSafe(record.date, '–')}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditingIndex(originalIndex)} className="h-8 w-8 p-0">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir esta evolução?
                          <br />
                          <span className="font-medium mt-2 block">
                            {formatDateSafe(record.date, 'Data inválida')} - {record.exercise || 'Sem exercício'}
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(originalIndex)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              {record.observations && (
                <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{record.observations}</p>
              )}
              <div className="grid grid-cols-3 gap-x-3 gap-y-2 text-sm">
                <div>
                  <span className="text-xs text-muted-foreground block">Carga</span>
                  <span className="font-medium">
                    {record.load !== null && record.load !== undefined
                      ? `${record.load} ${record.load_unit || 'Kg'}`
                      : '–'}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block">Reps</span>
                  <span className="font-medium">{record.reps ?? '–'}</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block">Resultado</span>
                  <span className="font-medium">{record.result || '–'}</span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  // Função para renderizar card mobile de antropometria
  const renderMobileAnthropometryCard = (record: EditablePhysiotherapyEvolution, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingAnthropometryIndex === originalIndex;

    const measurementTypeLabels: Record<string, string> = {
      peso: 'Peso',
      altura: 'Altura',
      circunferencia_cintura: 'Circunferência da Cintura',
      circunferencia_quadril: 'Circunferência do Quadril',
      imc: 'IMC',
      gordura_corporal: 'Gordura Corporal'
    };

    return (
      <Card key={`${dateKey}-anthro-${originalIndex}-mobile`} className="mb-3">
        <CardContent className="p-4">
          {isEditing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={editedAnthropometryRecords[originalIndex]?.date || ''}
                  onChange={(e) => {
                    const updated = [...editedAnthropometryRecords];
                    updated[originalIndex] = { ...updated[originalIndex], date: e.target.value };
                    setEditedAnthropometryRecords(updated);
                  }}
                  className="h-9"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Dado</Label>
                  <Input
                    value={editedAnthropometryRecords[originalIndex]?.measurement_type || ''}
                    onChange={(e) => {
                      const updated = [...editedAnthropometryRecords];
                      updated[originalIndex] = { ...updated[originalIndex], measurement_type: e.target.value || undefined };
                      setEditedAnthropometryRecords(updated);
                    }}
                    placeholder="Ex: Peso, Altura"
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Medida</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editedAnthropometryRecords[originalIndex]?.value ?? ''}
                    onChange={(e) => {
                      const updated = [...editedAnthropometryRecords];
                      updated[originalIndex] = { ...updated[originalIndex], value: e.target.value ? parseFloat(e.target.value) : undefined };
                      setEditedAnthropometryRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Unidade</Label>
                <Input
                  value={editedAnthropometryRecords[originalIndex]?.unit || ''}
                  onChange={(e) => {
                    const updated = [...editedAnthropometryRecords];
                    updated[originalIndex] = { ...updated[originalIndex], unit: e.target.value || undefined };
                    setEditedAnthropometryRecords(updated);
                  }}
                  placeholder="kg, cm, m, %"
                  className="h-9"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => handleSaveAnthropometry(originalIndex)} disabled={isSavingAnthropometry} className="flex-1">
                  {isSavingAnthropometry ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelAnthropometry} disabled={isSavingAnthropometry} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-semibold text-sm leading-tight">
                    {measurementTypeLabels[record.measurement_type || ''] || record.measurement_type || 'Medida'}
                  </h4>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Calendar className="h-3 w-3" />
                    {formatDateSafe(record.date, '–')}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditingAnthropometryIndex(originalIndex)} className="h-8 w-8 p-0">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir esta medida antropométrica?
                          <br />
                          <span className="font-medium mt-2 block">
                            {formatDateSafe(record.date, 'Data inválida')} - {measurementTypeLabels[record.measurement_type || ''] || record.measurement_type || 'Medida'}
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteAnthropometry(originalIndex)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 text-sm">
                <div>
                  <span className="text-xs text-muted-foreground block">Valor</span>
                  <span className="font-semibold">{record.value ?? '–'}</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block">Unidade</span>
                  <span>{record.unit || '–'}</span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  // Função para renderizar linha de teste SPPB (desktop)
  const renderSppbRow = (record: EditablePhysiotherapyEvolution, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingSppbIndex === originalIndex;

    return (
      <React.Fragment key={`${dateKey}-sppb-${originalIndex}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={10} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Data</Label>
                    <Input
                      type="date"
                      value={editedSppbRecords[originalIndex]?.date || ''}
                      onChange={(e) => {
                        const updated = [...editedSppbRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          date: e.target.value
                        };
                        setEditedSppbRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Nome do Teste</Label>
                    <Input
                      value={editedSppbRecords[originalIndex]?.test_name || ''}
                      onChange={(e) => {
                        const updated = [...editedSppbRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          test_name: e.target.value || null
                        };
                        setEditedSppbRecords(updated);
                      }}
                      placeholder="Ex: Sentar/Levantar – 5x"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Tempo (s)</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedSppbRecords[originalIndex]?.time ?? ''}
                      onChange={(e) => {
                        const updated = [...editedSppbRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          time: e.target.value ? parseFloat(e.target.value) : null
                        };
                        setEditedSppbRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Uso de Braços</Label>
                    <Select
                      value={editedSppbRecords[originalIndex]?.arms_used || ''}
                      onValueChange={(value) => {
                        const updated = [...editedSppbRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          arms_used: value || null
                        };
                        setEditedSppbRecords(updated);
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sim">Sim</SelectItem>
                        <SelectItem value="Não">Não</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Pontuação</Label>
                    <Input
                      type="number"
                      value={editedSppbRecords[originalIndex]?.score ?? ''}
                      onChange={(e) => {
                        const updated = [...editedSppbRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          score: e.target.value ? parseInt(e.target.value) : null
                        };
                        setEditedSppbRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Pontuação Equilíbrio</Label>
                    <Input
                      type="number"
                      value={editedSppbRecords[originalIndex]?.balance_score ?? ''}
                      onChange={(e) => {
                        const updated = [...editedSppbRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          balance_score: e.target.value ? parseInt(e.target.value) : null
                        };
                        setEditedSppbRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Pontuação Marcha</Label>
                    <Input
                      type="number"
                      value={editedSppbRecords[originalIndex]?.gait_score ?? ''}
                      onChange={(e) => {
                        const updated = [...editedSppbRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          gait_score: e.target.value ? parseInt(e.target.value) : null
                        };
                        setEditedSppbRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Pontuação Cadeira</Label>
                    <Input
                      type="number"
                      value={editedSppbRecords[originalIndex]?.chair_score ?? ''}
                      onChange={(e) => {
                        const updated = [...editedSppbRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          chair_score: e.target.value ? parseInt(e.target.value) : null
                        };
                        setEditedSppbRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Pontuação Total</Label>
                    <Input
                      type="number"
                      value={editedSppbRecords[originalIndex]?.total_score ?? ''}
                      onChange={(e) => {
                        const updated = [...editedSppbRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          total_score: e.target.value ? parseInt(e.target.value) : null
                        };
                        setEditedSppbRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Classificação</Label>
                    <Input
                      value={editedSppbRecords[originalIndex]?.classification || ''}
                      onChange={(e) => {
                        const updated = [...editedSppbRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          classification: e.target.value || null
                        };
                        setEditedSppbRecords(updated);
                      }}
                      placeholder="Ex: Baixa performance física"
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSaveSppb(originalIndex)} disabled={isSavingSppb}>
                    {isSavingSppb ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelSppb} disabled={isSavingSppb}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground w-[120px]">
              <div className="truncate">{formatDateSafe(record.date, '–')}</div>
            </td>
            <td className="p-3 w-[200px]">
              <div className="text-sm font-medium truncate" title={record.test_name || ''}>
                {record.test_name || '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.time !== null && record.time !== undefined ? `${record.time}s` : '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.arms_used || '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.score !== null && record.score !== undefined ? record.score : '–'}
              </div>
            </td>
            <td className="p-3 w-[120px]">
              <div className="text-sm truncate">
                {record.balance_score !== null && record.balance_score !== undefined ? record.balance_score : '–'}
              </div>
            </td>
            <td className="p-3 w-[120px]">
              <div className="text-sm truncate">
                {record.gait_score !== null && record.gait_score !== undefined ? record.gait_score : '–'}
              </div>
            </td>
            <td className="p-3 w-[120px]">
              <div className="text-sm truncate">
                {record.chair_score !== null && record.chair_score !== undefined ? record.chair_score : '–'}
              </div>
            </td>
            <td className="p-3 w-[150px]">
              <div className="text-sm truncate">
                {record.total_score !== null && record.total_score !== undefined ? record.total_score : '–'}
              </div>
            </td>
            <td className="p-3 w-[200px]">
              <div className="text-sm truncate" title={record.classification || ''}>
                {record.classification || '–'}
              </div>
            </td>
            <td className="p-3 text-right w-[120px]">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingSppbIndex(originalIndex)}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir este teste SPPB?
                        <br />
                        <span className="font-medium mt-2 block">
                          {formatDateSafe(record.date, 'Data inválida')} - {record.test_name || 'Sem nome'}
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDeleteSppb(originalIndex)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar linha de teste de dinamometria (desktop)
  const renderDynamometryRow = (record: EditablePhysiotherapyEvolution, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingDynamometryIndex === originalIndex;

    return (
      <React.Fragment key={`${dateKey}-dyn-${originalIndex}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={9} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Data</Label>
                    <Input
                      type="date"
                      value={editedDynamometryRecords[originalIndex]?.date || ''}
                      onChange={(e) => {
                        const updated = [...editedDynamometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          date: e.target.value
                        };
                        setEditedDynamometryRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Nome do Teste</Label>
                    <Input
                      value={editedDynamometryRecords[originalIndex]?.test_name || ''}
                      onChange={(e) => {
                        const updated = [...editedDynamometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          test_name: e.target.value || null
                        };
                        setEditedDynamometryRecords(updated);
                      }}
                      placeholder="Ex: Dinamometria de Preensão Manual"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Mão Dominante</Label>
                    <Select
                      value={editedDynamometryRecords[originalIndex]?.dominant_hand || ''}
                      onValueChange={(value) => {
                        const updated = [...editedDynamometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          dominant_hand: value || null
                        };
                        setEditedDynamometryRecords(updated);
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Direita">Direita</SelectItem>
                        <SelectItem value="Esquerda">Esquerda</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Valor Direito</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedDynamometryRecords[originalIndex]?.right_value ?? ''}
                      onChange={(e) => {
                        const updated = [...editedDynamometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          right_value: e.target.value ? parseFloat(e.target.value) : null
                        };
                        setEditedDynamometryRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Valor Esquerdo</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedDynamometryRecords[originalIndex]?.left_value ?? ''}
                      onChange={(e) => {
                        const updated = [...editedDynamometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          left_value: e.target.value ? parseFloat(e.target.value) : null
                        };
                        setEditedDynamometryRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Unidade</Label>
                    <Input
                      value={editedDynamometryRecords[originalIndex]?.unit || ''}
                      onChange={(e) => {
                        const updated = [...editedDynamometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          unit: e.target.value || null
                        };
                        setEditedDynamometryRecords(updated);
                      }}
                      placeholder="kgf"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Assimetria (%)</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedDynamometryRecords[originalIndex]?.asymmetry ?? ''}
                      onChange={(e) => {
                        const updated = [...editedDynamometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          asymmetry: e.target.value ? parseFloat(e.target.value) : null
                        };
                        setEditedDynamometryRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Classificação</Label>
                    <Input
                      value={editedDynamometryRecords[originalIndex]?.classification || ''}
                      onChange={(e) => {
                        const updated = [...editedDynamometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          classification: e.target.value || null
                        };
                        setEditedDynamometryRecords(updated);
                      }}
                      placeholder="Ex: Força reduzida para faixa etária"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Observação</Label>
                    <Input
                      value={editedDynamometryRecords[originalIndex]?.observation || ''}
                      onChange={(e) => {
                        const updated = [...editedDynamometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          observation: e.target.value || null
                        };
                        setEditedDynamometryRecords(updated);
                      }}
                      placeholder="Observações adicionais"
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSaveDynamometry(originalIndex)} disabled={isSavingDynamometry}>
                    {isSavingDynamometry ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelDynamometry} disabled={isSavingDynamometry}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground w-[120px]">
              <div className="truncate">{formatDateSafe(record.date, '–')}</div>
            </td>
            <td className="p-3 w-[200px]">
              <div className="text-sm font-medium truncate" title={record.test_name || ''}>
                {record.test_name || '–'}
              </div>
            </td>
            <td className="p-3 w-[120px]">
              <div className="text-sm truncate">
                {record.dominant_hand || '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.right_value !== null && record.right_value !== undefined ? record.right_value : '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.left_value !== null && record.left_value !== undefined ? record.left_value : '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.unit || '–'}
              </div>
            </td>
            <td className="p-3 w-[150px]">
              <div className="text-sm truncate" title={record.classification || ''}>
                {record.classification || '–'}
              </div>
            </td>
            <td className="p-3 w-[150px]">
              <div className="text-sm truncate" title={record.observation || ''}>
                {record.observation || '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.asymmetry !== null && record.asymmetry !== undefined ? `${record.asymmetry}%` : '–'}
              </div>
            </td>
            <td className="p-3 text-right w-[120px]">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingDynamometryIndex(originalIndex)}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir este teste de dinamometria?
                        <br />
                        <span className="font-medium mt-2 block">
                          {formatDateSafe(record.date, 'Data inválida')} - {record.test_name || 'Sem nome'}
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDeleteDynamometry(originalIndex)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar linha de goniometria (desktop)
  const renderGoniometryRow = (record: EditablePhysiotherapyEvolution, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingGoniometryIndex === originalIndex;

    return (
      <React.Fragment key={`${dateKey}-gon-${originalIndex}`}>
        {isEditing ? (
          <tr className="border-b border-border">
            <td colSpan={6} className="p-3 bg-muted/30">
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Data</Label>
                    <Input
                      type="date"
                      value={editedGoniometryRecords[originalIndex]?.date || ''}
                      onChange={(e) => {
                        const updated = [...editedGoniometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          date: e.target.value
                        };
                        setEditedGoniometryRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Tipo de Movimento</Label>
                    <Input
                      value={editedGoniometryRecords[originalIndex]?.movement_type || ''}
                      onChange={(e) => {
                        const updated = [...editedGoniometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          movement_type: e.target.value || null
                        };
                        setEditedGoniometryRecords(updated);
                      }}
                      placeholder="Ex: Rotação, Inclinação, Flexão"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Parte do Corpo</Label>
                    <Input
                      value={editedGoniometryRecords[originalIndex]?.body_part || ''}
                      onChange={(e) => {
                        const updated = [...editedGoniometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          body_part: e.target.value || null
                        };
                        setEditedGoniometryRecords(updated);
                      }}
                      placeholder="Ex: cervical, ombro, joelho"
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Lado</Label>
                    <Select
                      value={editedGoniometryRecords[originalIndex]?.side || ''}
                      onValueChange={(value) => {
                        const updated = [...editedGoniometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          side: value || null
                        };
                        setEditedGoniometryRecords(updated);
                      }}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="direita">Direita</SelectItem>
                        <SelectItem value="esquerda">Esquerda</SelectItem>
                        <SelectItem value="bilateral">Bilateral</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Valor (graus)</Label>
                    <Input
                      type="number"
                      step="any"
                      value={editedGoniometryRecords[originalIndex]?.goniometry_value ?? ''}
                      onChange={(e) => {
                        const updated = [...editedGoniometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          goniometry_value: e.target.value ? parseFloat(e.target.value) : null
                        };
                        setEditedGoniometryRecords(updated);
                      }}
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Unidade</Label>
                    <Input
                      value={editedGoniometryRecords[originalIndex]?.goniometry_unit || ''}
                      onChange={(e) => {
                        const updated = [...editedGoniometryRecords];
                        updated[originalIndex] = {
                          ...updated[originalIndex],
                          goniometry_unit: e.target.value || null
                        };
                        setEditedGoniometryRecords(updated);
                      }}
                      placeholder="graus ou °"
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={() => handleSaveGoniometry(originalIndex)} disabled={isSavingGoniometry}>
                    {isSavingGoniometry ? "Salvando..." : "Salvar"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelGoniometry} disabled={isSavingGoniometry}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </td>
          </tr>
        ) : (
          <tr className="border-b border-border hover:bg-muted/30 transition-colors">
            <td className="p-3 text-sm text-muted-foreground w-[120px]">
              <div className="truncate">{formatDateSafe(record.date, '–')}</div>
            </td>
            <td className="p-3 w-[150px]">
              <div className="text-sm font-medium truncate" title={record.movement_type || ''}>
                {record.movement_type || '–'}
              </div>
            </td>
            <td className="p-3 w-[150px]">
              <div className="text-sm truncate" title={record.body_part || ''}>
                {record.body_part || '–'}
              </div>
            </td>
            <td className="p-3 w-[100px]">
              <div className="text-sm truncate">
                {record.side || '–'}
              </div>
            </td>
            <td className="p-3 w-[120px]">
              <div className="text-sm truncate">
                {record.goniometry_value !== null && record.goniometry_value !== undefined 
                  ? `${record.goniometry_value} ${record.goniometry_unit || '°'}` 
                  : '–'}
              </div>
            </td>
            <td className="p-3 text-right w-[120px]">
              <div className="flex gap-1 justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingGoniometryIndex(originalIndex)}
                  className="h-7 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir este registro de goniometria?
                        <br />
                        <span className="font-medium mt-2 block">
                          {formatDateSafe(record.date, 'Data inválida')} - {record.movement_type || 'Sem tipo'} - {record.body_part || 'Sem parte do corpo'}
                        </span>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDeleteGoniometry(originalIndex)}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Função para renderizar card mobile de teste SPPB
  const renderMobileSppbCard = (record: EditablePhysiotherapyEvolution, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingSppbIndex === originalIndex;

    return (
      <Card key={`${dateKey}-sppb-${originalIndex}-mobile`} className="mb-3">
        <CardContent className="p-4">
          {isEditing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={editedSppbRecords[originalIndex]?.date || ''}
                  onChange={(e) => {
                    const updated = [...editedSppbRecords];
                    updated[originalIndex] = { ...updated[originalIndex], date: e.target.value };
                    setEditedSppbRecords(updated);
                  }}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Nome do Teste</Label>
                <Input
                  value={editedSppbRecords[originalIndex]?.test_name || ''}
                  onChange={(e) => {
                    const updated = [...editedSppbRecords];
                    updated[originalIndex] = { ...updated[originalIndex], test_name: e.target.value || null };
                    setEditedSppbRecords(updated);
                  }}
                  placeholder="Ex: Sentar/Levantar – 5x"
                  className="h-9"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Tempo (s)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editedSppbRecords[originalIndex]?.time ?? ''}
                    onChange={(e) => {
                      const updated = [...editedSppbRecords];
                      updated[originalIndex] = { ...updated[originalIndex], time: e.target.value ? parseFloat(e.target.value) : null };
                      setEditedSppbRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Uso de Braços</Label>
                  <Select
                    value={editedSppbRecords[originalIndex]?.arms_used || ''}
                    onValueChange={(value) => {
                      const updated = [...editedSppbRecords];
                      updated[originalIndex] = { ...updated[originalIndex], arms_used: value || null };
                      setEditedSppbRecords(updated);
                    }}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Sim">Sim</SelectItem>
                      <SelectItem value="Não">Não</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Pontuação</Label>
                  <Input
                    type="number"
                    value={editedSppbRecords[originalIndex]?.score ?? ''}
                    onChange={(e) => {
                      const updated = [...editedSppbRecords];
                      updated[originalIndex] = { ...updated[originalIndex], score: e.target.value ? parseInt(e.target.value) : null };
                      setEditedSppbRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Pontuação Total</Label>
                  <Input
                    type="number"
                    value={editedSppbRecords[originalIndex]?.total_score ?? ''}
                    onChange={(e) => {
                      const updated = [...editedSppbRecords];
                      updated[originalIndex] = { ...updated[originalIndex], total_score: e.target.value ? parseInt(e.target.value) : null };
                      setEditedSppbRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Classificação</Label>
                <Input
                  value={editedSppbRecords[originalIndex]?.classification || ''}
                  onChange={(e) => {
                    const updated = [...editedSppbRecords];
                    updated[originalIndex] = { ...updated[originalIndex], classification: e.target.value || null };
                    setEditedSppbRecords(updated);
                  }}
                  placeholder="Ex: Baixa performance física"
                  className="h-9"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => handleSaveSppb(originalIndex)} disabled={isSavingSppb} className="flex-1">
                  {isSavingSppb ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelSppb} disabled={isSavingSppb} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-semibold text-sm leading-tight">
                    {record.test_name || 'Teste não especificado'}
                  </h4>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Calendar className="h-3 w-3" />
                    {formatDateSafe(record.date, '–')}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditingSppbIndex(originalIndex)} className="h-8 w-8 p-0">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir este teste SPPB?
                          <br />
                          <span className="font-medium mt-2 block">
                            {formatDateSafe(record.date, 'Data inválida')} - {record.test_name || 'Sem nome'}
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteSppb(originalIndex)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {record.time !== null && record.time !== undefined && (
                  <div>
                    <span className="text-xs text-muted-foreground block">Tempo</span>
                    <span className="font-semibold">{record.time}s</span>
                  </div>
                )}
                {record.arms_used && (
                  <div>
                    <span className="text-xs text-muted-foreground block">Uso de Braços</span>
                    <span className="font-semibold">{record.arms_used}</span>
                  </div>
                )}
                {record.score !== null && record.score !== undefined && (
                  <div>
                    <span className="text-xs text-muted-foreground block">Pontuação</span>
                    <span className="font-semibold">{record.score}</span>
                  </div>
                )}
                {record.total_score !== null && record.total_score !== undefined && (
                  <div>
                    <span className="text-xs text-muted-foreground block">Pontuação Total</span>
                    <span className="font-semibold">{record.total_score}</span>
                  </div>
                )}
                {record.classification && (
                  <div className="col-span-2">
                    <span className="text-xs text-muted-foreground block">Classificação</span>
                    <span className="font-semibold">{record.classification}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  // Função para renderizar card mobile de teste de dinamometria
  const renderMobileDynamometryCard = (record: EditablePhysiotherapyEvolution, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingDynamometryIndex === originalIndex;

    return (
      <Card key={`${dateKey}-dyn-${originalIndex}-mobile`} className="mb-3">
        <CardContent className="p-4">
          {isEditing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={editedDynamometryRecords[originalIndex]?.date || ''}
                  onChange={(e) => {
                    const updated = [...editedDynamometryRecords];
                    updated[originalIndex] = { ...updated[originalIndex], date: e.target.value };
                    setEditedDynamometryRecords(updated);
                  }}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Nome do Teste</Label>
                <Input
                  value={editedDynamometryRecords[originalIndex]?.test_name || ''}
                  onChange={(e) => {
                    const updated = [...editedDynamometryRecords];
                    updated[originalIndex] = { ...updated[originalIndex], test_name: e.target.value || null };
                    setEditedDynamometryRecords(updated);
                  }}
                  placeholder="Ex: Dinamometria de Preensão Manual"
                  className="h-9"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Valor Direito</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editedDynamometryRecords[originalIndex]?.right_value ?? ''}
                    onChange={(e) => {
                      const updated = [...editedDynamometryRecords];
                      updated[originalIndex] = { ...updated[originalIndex], right_value: e.target.value ? parseFloat(e.target.value) : null };
                      setEditedDynamometryRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Valor Esquerdo</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editedDynamometryRecords[originalIndex]?.left_value ?? ''}
                    onChange={(e) => {
                      const updated = [...editedDynamometryRecords];
                      updated[originalIndex] = { ...updated[originalIndex], left_value: e.target.value ? parseFloat(e.target.value) : null };
                      setEditedDynamometryRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Unidade</Label>
                  <Input
                    value={editedDynamometryRecords[originalIndex]?.unit || ''}
                    onChange={(e) => {
                      const updated = [...editedDynamometryRecords];
                      updated[originalIndex] = { ...updated[originalIndex], unit: e.target.value || null };
                      setEditedDynamometryRecords(updated);
                    }}
                    placeholder="kgf"
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Assimetria (%)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editedDynamometryRecords[originalIndex]?.asymmetry ?? ''}
                    onChange={(e) => {
                      const updated = [...editedDynamometryRecords];
                      updated[originalIndex] = { ...updated[originalIndex], asymmetry: e.target.value ? parseFloat(e.target.value) : null };
                      setEditedDynamometryRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Classificação</Label>
                <Input
                  value={editedDynamometryRecords[originalIndex]?.classification || ''}
                  onChange={(e) => {
                    const updated = [...editedDynamometryRecords];
                    updated[originalIndex] = { ...updated[originalIndex], classification: e.target.value || null };
                    setEditedDynamometryRecords(updated);
                  }}
                  placeholder="Ex: Força reduzida para faixa etária"
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Observação</Label>
                <Textarea
                  value={editedDynamometryRecords[originalIndex]?.observation || ''}
                  onChange={(e) => {
                    const updated = [...editedDynamometryRecords];
                    updated[originalIndex] = { ...updated[originalIndex], observation: e.target.value || null };
                    setEditedDynamometryRecords(updated);
                  }}
                  placeholder="Observações adicionais"
                  className="min-h-[60px]"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => handleSaveDynamometry(originalIndex)} disabled={isSavingDynamometry} className="flex-1">
                  {isSavingDynamometry ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelDynamometry} disabled={isSavingDynamometry} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-semibold text-sm leading-tight">
                    {record.test_name || 'Teste não especificado'}
                  </h4>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Calendar className="h-3 w-3" />
                    {formatDateSafe(record.date, '–')}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditingDynamometryIndex(originalIndex)} className="h-8 w-8 p-0">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir este teste de dinamometria?
                          <br />
                          <span className="font-medium mt-2 block">
                            {formatDateSafe(record.date, 'Data inválida')} - {record.test_name || 'Sem nome'}
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteDynamometry(originalIndex)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {record.right_value !== null && record.right_value !== undefined && (
                  <div>
                    <span className="text-xs text-muted-foreground block">Direito</span>
                    <span className="font-semibold">{record.right_value} {record.unit || ''}</span>
                  </div>
                )}
                {record.left_value !== null && record.left_value !== undefined && (
                  <div>
                    <span className="text-xs text-muted-foreground block">Esquerdo</span>
                    <span className="font-semibold">{record.left_value} {record.unit || ''}</span>
                  </div>
                )}
                {record.asymmetry !== null && record.asymmetry !== undefined && (
                  <div>
                    <span className="text-xs text-muted-foreground block">Assimetria</span>
                    <span className="font-semibold">{record.asymmetry}%</span>
                  </div>
                )}
                {record.classification && (
                  <div className="col-span-2">
                    <span className="text-xs text-muted-foreground block">Classificação</span>
                    <span className="font-semibold">{record.classification}</span>
                  </div>
                )}
                {record.observation && (
                  <div className="col-span-2">
                    <span className="text-xs text-muted-foreground block">Observação</span>
                    <span className="text-xs">{record.observation}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  // Função para renderizar card mobile de goniometria
  const renderMobileGoniometryCard = (record: EditablePhysiotherapyEvolution, groupIndex: number, dateKey: string) => {
    const originalIndex = record._index ?? groupIndex;
    const isEditing = editingGoniometryIndex === originalIndex;

    return (
      <Card key={`${dateKey}-gon-${originalIndex}-mobile`} className="mb-3">
        <CardContent className="p-4">
          {isEditing ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={editedGoniometryRecords[originalIndex]?.date || ''}
                  onChange={(e) => {
                    const updated = [...editedGoniometryRecords];
                    updated[originalIndex] = { ...updated[originalIndex], date: e.target.value };
                    setEditedGoniometryRecords(updated);
                  }}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Tipo de Movimento</Label>
                <Input
                  value={editedGoniometryRecords[originalIndex]?.movement_type || ''}
                  onChange={(e) => {
                    const updated = [...editedGoniometryRecords];
                    updated[originalIndex] = { ...updated[originalIndex], movement_type: e.target.value || null };
                    setEditedGoniometryRecords(updated);
                  }}
                  placeholder="Ex: Rotação, Inclinação"
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Parte do Corpo</Label>
                <Input
                  value={editedGoniometryRecords[originalIndex]?.body_part || ''}
                  onChange={(e) => {
                    const updated = [...editedGoniometryRecords];
                    updated[originalIndex] = { ...updated[originalIndex], body_part: e.target.value || null };
                    setEditedGoniometryRecords(updated);
                  }}
                  placeholder="Ex: cervical, ombro, joelho"
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Lado</Label>
                <Select
                  value={editedGoniometryRecords[originalIndex]?.side || ''}
                  onValueChange={(value) => {
                    const updated = [...editedGoniometryRecords];
                    updated[originalIndex] = { ...updated[originalIndex], side: value || null };
                    setEditedGoniometryRecords(updated);
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direita">Direita</SelectItem>
                    <SelectItem value="esquerda">Esquerda</SelectItem>
                    <SelectItem value="bilateral">Bilateral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Valor (graus)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={editedGoniometryRecords[originalIndex]?.goniometry_value ?? ''}
                    onChange={(e) => {
                      const updated = [...editedGoniometryRecords];
                      updated[originalIndex] = { ...updated[originalIndex], goniometry_value: e.target.value ? parseFloat(e.target.value) : null };
                      setEditedGoniometryRecords(updated);
                    }}
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Unidade</Label>
                  <Input
                    value={editedGoniometryRecords[originalIndex]?.goniometry_unit || ''}
                    onChange={(e) => {
                      const updated = [...editedGoniometryRecords];
                      updated[originalIndex] = { ...updated[originalIndex], goniometry_unit: e.target.value || null };
                      setEditedGoniometryRecords(updated);
                    }}
                    placeholder="graus ou °"
                    className="h-9"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={() => handleSaveGoniometry(originalIndex)} disabled={isSavingGoniometry} className="flex-1">
                  {isSavingGoniometry ? "Salvando..." : "Salvar"}
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancelGoniometry} disabled={isSavingGoniometry} className="flex-1">
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-semibold text-sm leading-tight">
                    {record.movement_type || 'Movimento não especificado'} - {record.body_part || 'Parte não especificada'}
                  </h4>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                    <Calendar className="h-3 w-3" />
                    {formatDateSafe(record.date, '–')}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditingGoniometryIndex(originalIndex)} className="h-8 w-8 p-0">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                        <AlertDialogDescription>
                          Tem certeza que deseja excluir este registro de goniometria?
                          <br />
                          <span className="font-medium mt-2 block">
                            {formatDateSafe(record.date, 'Data inválida')} - {record.movement_type || 'Sem tipo'} - {record.body_part || 'Sem parte do corpo'}
                          </span>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteGoniometry(originalIndex)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Excluir
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {record.side && (
                  <div>
                    <span className="text-xs text-muted-foreground block">Lado</span>
                    <span className="font-semibold capitalize">{record.side}</span>
                  </div>
                )}
                {record.goniometry_value !== null && record.goniometry_value !== undefined && (
                  <div>
                    <span className="text-xs text-muted-foreground block">Valor</span>
                    <span className="font-semibold">{record.goniometry_value} {record.goniometry_unit || '°'}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="bg-background border-t">
      {/* Seção: Evolução Fisioterapêutica */}
      {physioRecords.length > 0 && (
        <div className="mb-6">
          {/* Mobile: Cards View */}
          {isMobile ? (
            <div className="p-3">
              {sortedDates.map((monthYearKey) => {
                const dateGroup = groupedByDate[monthYearKey];
                const dateRecords = dateGroup.records;
                const displayKey = dateGroup.displayKey;
                const defaultExpanded = expandAllByDefault;
                const manualState = expandedDateGroups.get(monthYearKey);
                const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                return (
                  <div key={monthYearKey} className="mb-4">
                    <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(monthYearKey, open)}>
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-2 text-xs font-semibold text-primary cursor-pointer bg-primary/5 hover:bg-primary/10 rounded-lg px-3 py-2 mb-2 transition-colors">
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          <Activity className="h-4 w-4" />
                          <span>📅 {displayKey}</span>
                          <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                          <Badge variant="secondary">
                            {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {dateRecords.map((record, groupIndex) => renderMobilePhysioCard(record, groupIndex, monthYearKey))}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop: Table View */
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '200px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Data
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Observações
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Exercício
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Carga
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Unidade
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Repetições
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Resultado
                    </th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDates.map((monthYearKey) => {
                    const dateGroup = groupedByDate[monthYearKey];
                    const dateRecords = dateGroup.records;
                    const displayKey = dateGroup.displayKey;
                    const defaultExpanded = expandAllByDefault;
                    const manualState = expandedDateGroups.get(monthYearKey);
                    const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                    return (
                      <React.Fragment key={monthYearKey}>
                        <tr className="bg-primary/5 border-y-2 border-primary/20">
                          <td colSpan={8} className="p-2">
                            <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroup(monthYearKey, open)}>
                              <CollapsibleTrigger asChild>
                                <div className="flex items-center gap-2 text-xs font-semibold text-primary cursor-pointer hover:bg-primary/10 rounded px-2 py-1 transition-colors w-full">
                                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  <Activity className="h-4 w-4" />
                                  <span>📅 {displayKey}</span>
                                  <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                                  <Badge variant="secondary">
                                    {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                  </Badge>
                                </div>
                              </CollapsibleTrigger>
                            </Collapsible>
                          </td>
                        </tr>

                        {isExpanded && (
                          <>
                            {dateRecords.map((record, groupIndex) => renderRecordRow(record, groupIndex, monthYearKey))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
            Total: {physioRecords.length} registro(s) de evolução fisioterapêutica em {sortedDates.length} mês(es)/ano(s)
          </div>
        </div>
      )}

      {/* Seção: Antropometria */}
      {anthropometryRecords.length > 0 && (
        <div>
          {/* Mobile: Cards View */}
          {isMobile ? (
            <div className="p-3">
              {sortedDatesAnthropometry.map((monthYearKey) => {
                const dateGroup = groupedByDateAnthropometry[monthYearKey];
                const dateRecords = dateGroup.records;
                const displayKey = dateGroup.displayKey;
                const defaultExpanded = expandAllByDefault;
                const manualState = expandedDateGroupsAnthropometry.get(monthYearKey);
                const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                return (
                  <div key={monthYearKey} className="mb-4">
                    <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroupAnthropometry(monthYearKey, open)}>
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 cursor-pointer bg-blue-500/5 hover:bg-blue-500/10 rounded-lg px-3 py-2 mb-2 transition-colors">
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          <Ruler className="h-4 w-4" />
                          <span>📅 {displayKey}</span>
                          <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                          <Badge variant="secondary">
                            {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {dateRecords.map((record, groupIndex) => renderMobileAnthropometryCard(record, groupIndex, monthYearKey))}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop: Table View */
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '200px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Data
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Dado
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Medida
                    </th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Unidade
                    </th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDatesAnthropometry.map((monthYearKey) => {
                    const dateGroup = groupedByDateAnthropometry[monthYearKey];
                    const dateRecords = dateGroup.records;
                    const displayKey = dateGroup.displayKey;
                    const defaultExpanded = expandAllByDefault;
                    const manualState = expandedDateGroupsAnthropometry.get(monthYearKey);
                    const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                    return (
                      <React.Fragment key={monthYearKey}>
                        <tr className="bg-blue-500/5 border-y-2 border-blue-500/20">
                          <td colSpan={5} className="p-2">
                            <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroupAnthropometry(monthYearKey, open)}>
                              <CollapsibleTrigger asChild>
                                <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 cursor-pointer hover:bg-blue-500/10 rounded px-2 py-1 transition-colors w-full">
                                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  <Ruler className="h-4 w-4" />
                                  <span>📅 {displayKey}</span>
                                  <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                                  <Badge variant="secondary">
                                    {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                  </Badge>
                                </div>
                              </CollapsibleTrigger>
                            </Collapsible>
                          </td>
                        </tr>

                        {isExpanded && (
                          <>
                            {dateRecords.map((record, groupIndex) => renderAnthropometryRow(record, groupIndex, monthYearKey))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
            Total: {anthropometryRecords.length} registro(s) de antropometria em {sortedDatesAnthropometry.length} mês(es)/ano(s)
          </div>
        </div>
      )}

      {/* Seção: Testes SPPB */}
      {sppbRecords.length > 0 && (
        <div className="mb-6">
          {/* Mobile: Cards View */}
          {isMobile ? (
            <div className="p-3">
              {sortedDatesSppb.map((monthYearKey) => {
                const dateGroup = groupedByDateSppb[monthYearKey];
                const dateRecords = dateGroup.records;
                const displayKey = dateGroup.displayKey;
                const defaultExpanded = expandAllByDefault;
                const manualState = expandedDateGroupsSppb.get(monthYearKey);
                const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                return (
                  <div key={monthYearKey} className="mb-4">
                    <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroupSppb(monthYearKey, open)}>
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-2 text-xs font-semibold text-green-600 cursor-pointer bg-green-500/5 hover:bg-green-500/10 rounded-lg px-3 py-2 mb-2 transition-colors">
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          <Activity className="h-4 w-4" />
                          <span>📅 {displayKey}</span>
                          <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                          <Badge variant="secondary">
                            {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {dateRecords.map((record, groupIndex) => renderMobileSppbCard(record, groupIndex, monthYearKey))}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop: Table View */
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '200px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '200px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Data</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Nome do Teste</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tempo</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Uso Braços</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Pontuação</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Equilíbrio</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Marcha</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Cadeira</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Total</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Classificação</th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDatesSppb.map((monthYearKey) => {
                    const dateGroup = groupedByDateSppb[monthYearKey];
                    const dateRecords = dateGroup.records;
                    const displayKey = dateGroup.displayKey;
                    const defaultExpanded = expandAllByDefault;
                    const manualState = expandedDateGroupsSppb.get(monthYearKey);
                    const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                    return (
                      <React.Fragment key={monthYearKey}>
                        <tr className="bg-green-500/5 border-y-2 border-green-500/20">
                          <td colSpan={11} className="p-2">
                            <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroupSppb(monthYearKey, open)}>
                              <CollapsibleTrigger asChild>
                                <div className="flex items-center gap-2 text-xs font-semibold text-green-600 cursor-pointer hover:bg-green-500/10 rounded px-2 py-1 transition-colors w-full">
                                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  <Activity className="h-4 w-4" />
                                  <span>📅 {displayKey}</span>
                                  <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                                  <Badge variant="secondary">
                                    {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                  </Badge>
                                </div>
                              </CollapsibleTrigger>
                            </Collapsible>
                          </td>
                        </tr>

                        {isExpanded && (
                          <>
                            {dateRecords.map((record, groupIndex) => renderSppbRow(record, groupIndex, monthYearKey))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
            Total: {sppbRecords.length} registro(s) de testes SPPB em {sortedDatesSppb.length} mês(es)/ano(s)
          </div>
        </div>
      )}

      {/* Seção: Testes de Dinamometria */}
      {dynamometryRecords.length > 0 && (
        <div>
          {/* Mobile: Cards View */}
          {isMobile ? (
            <div className="p-3">
              {sortedDatesDynamometry.map((monthYearKey) => {
                const dateGroup = groupedByDateDynamometry[monthYearKey];
                const dateRecords = dateGroup.records;
                const displayKey = dateGroup.displayKey;
                const defaultExpanded = expandAllByDefault;
                const manualState = expandedDateGroupsDynamometry.get(monthYearKey);
                const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                return (
                  <div key={monthYearKey} className="mb-4">
                    <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroupDynamometry(monthYearKey, open)}>
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-2 text-xs font-semibold text-purple-600 cursor-pointer bg-purple-500/5 hover:bg-purple-500/10 rounded-lg px-3 py-2 mb-2 transition-colors">
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          <Activity className="h-4 w-4" />
                          <span>📅 {displayKey}</span>
                          <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                          <Badge variant="secondary">
                            {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {dateRecords.map((record, groupIndex) => renderMobileDynamometryCard(record, groupIndex, monthYearKey))}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop: Table View */
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '200px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Data</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Nome do Teste</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Mão Dominante</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Direito</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Esquerdo</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Unidade</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Classificação</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Observação</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Assimetria</th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDatesDynamometry.map((monthYearKey) => {
                    const dateGroup = groupedByDateDynamometry[monthYearKey];
                    const dateRecords = dateGroup.records;
                    const displayKey = dateGroup.displayKey;
                    const defaultExpanded = expandAllByDefault;
                    const manualState = expandedDateGroupsDynamometry.get(monthYearKey);
                    const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                    return (
                      <React.Fragment key={monthYearKey}>
                        <tr className="bg-purple-500/5 border-y-2 border-purple-500/20">
                          <td colSpan={10} className="p-2">
                            <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroupDynamometry(monthYearKey, open)}>
                              <CollapsibleTrigger asChild>
                                <div className="flex items-center gap-2 text-xs font-semibold text-purple-600 cursor-pointer hover:bg-purple-500/10 rounded px-2 py-1 transition-colors w-full">
                                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  <Activity className="h-4 w-4" />
                                  <span>📅 {displayKey}</span>
                                  <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                                  <Badge variant="secondary">
                                    {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                  </Badge>
                                </div>
                              </CollapsibleTrigger>
                            </Collapsible>
                          </td>
                        </tr>

                        {isExpanded && (
                          <>
                            {dateRecords.map((record, groupIndex) => renderDynamometryRow(record, groupIndex, monthYearKey))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
            Total: {dynamometryRecords.length} registro(s) de testes de dinamometria em {sortedDatesDynamometry.length} mês(es)/ano(s)
          </div>
        </div>
      )}

      {/* Seção: Goniometria */}
      {goniometryRecords.length > 0 && (
        <div className="mb-6">
          {/* Mobile: Cards View */}
          {isMobile ? (
            <div className="p-3">
              {sortedDatesGoniometry.map((monthYearKey) => {
                const dateGroup = groupedByDateGoniometry[monthYearKey];
                const dateRecords = dateGroup.records;
                const displayKey = dateGroup.displayKey;
                const defaultExpanded = expandAllByDefault;
                const manualState = expandedDateGroupsGoniometry.get(monthYearKey);
                const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                return (
                  <div key={monthYearKey} className="mb-4">
                    <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroupGoniometry(monthYearKey, open)}>
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 cursor-pointer bg-blue-500/5 hover:bg-blue-500/10 rounded-lg px-3 py-2 mb-2 transition-colors">
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                          <Activity className="h-4 w-4" />
                          <span>📅 {displayKey}</span>
                          <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                          <Badge variant="secondary">
                            {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        {dateRecords.map((record, groupIndex) => renderMobileGoniometryCard(record, groupIndex, monthYearKey))}
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop: Table View */
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '150px' }} />
                  <col style={{ width: '100px' }} />
                  <col style={{ width: '120px' }} />
                  <col style={{ width: '120px' }} />
                </colgroup>
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Data</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tipo de Movimento</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Parte do Corpo</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Lado</th>
                    <th className="p-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Valor</th>
                    <th className="p-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDatesGoniometry.map((monthYearKey) => {
                    const dateGroup = groupedByDateGoniometry[monthYearKey];
                    const dateRecords = dateGroup.records;
                    const displayKey = dateGroup.displayKey;
                    const defaultExpanded = expandAllByDefault;
                    const manualState = expandedDateGroupsGoniometry.get(monthYearKey);
                    const isExpanded = manualState !== undefined ? manualState : defaultExpanded;

                    return (
                      <React.Fragment key={monthYearKey}>
                        <tr className="bg-blue-500/5 border-y-2 border-blue-500/20">
                          <td colSpan={6} className="p-2">
                            <Collapsible open={isExpanded} onOpenChange={(open) => toggleDateGroupGoniometry(monthYearKey, open)}>
                              <CollapsibleTrigger asChild>
                                <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 cursor-pointer hover:bg-blue-500/10 rounded px-2 py-1 transition-colors w-full">
                                  {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  <Activity className="h-4 w-4" />
                                  <span>📅 {displayKey}</span>
                                  <span className="ml-auto font-normal text-muted-foreground mr-2">{isExpanded ? '- Ver menos' : '+ Ver mais'}</span>
                                  <Badge variant="secondary">
                                    {dateRecords.length} registro{dateRecords.length !== 1 ? 's' : ''}
                                  </Badge>
                                </div>
                              </CollapsibleTrigger>
                            </Collapsible>
                          </td>
                        </tr>

                        {isExpanded && (
                          <>
                            {dateRecords.map((record, groupIndex) => renderGoniometryRow(record, groupIndex, monthYearKey))}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="p-3 text-xs text-muted-foreground border-t border-border bg-muted/30">
            Total: {goniometryRecords.length} registro(s) de goniometria em {sortedDatesGoniometry.length} mês(es)/ano(s)
          </div>
        </div>
      )}
    </div>
  );
}

